/**
 * LandingPage.js - Marketing landing page for unauthenticated users
 * All inline styles per project convention (CRA global CSS collision avoidance)
 */

import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { sendContact, sendBetaRequest } from '../services/api';
import { useTheme } from '../context/ThemeContext';
import Logo from './Logo';

const BETA_MODE = process.env.REACT_APP_BETA_MODE === 'true';
const fontFamily = "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif";
const gradient = 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)';

function LandingPage({ onLogin, onSignup }) {
  const { theme, toggleTheme, colors } = useTheme();
  const betaFormRef = useRef(null);
  const [contactName, setContactName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactMessage, setContactMessage] = useState('');
  const [contactSending, setContactSending] = useState(false);
  const [contactSent, setContactSent] = useState(false);
  const [contactError, setContactError] = useState('');

  // Beta request form state
  const [betaOrg, setBetaOrg] = useState('');
  const [betaDescription, setBetaDescription] = useState('');

  const scrollToBetaForm = () => {
    betaFormRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    if (!contactEmail.trim()) return;

    setContactSending(true);
    setContactError('');
    try {
      if (BETA_MODE) {
        await sendBetaRequest(contactName, contactEmail, betaOrg, betaDescription);
      } else {
        if (!contactMessage.trim()) return;
        await sendContact(contactName, contactEmail, contactMessage);
      }
      setContactSent(true);
      setContactName('');
      setContactEmail('');
      setContactMessage('');
      setBetaOrg('');
      setBetaDescription('');
    } catch (err) {
      setContactError(err.message);
    } finally {
      setContactSending(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', fontFamily, background: colors.bg, color: colors.text }}>
      {/* ── Hero ── */}
      <div style={{ background: gradient, padding: '0 2rem 5rem', color: '#fff' }}>
        <nav style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1.25rem 0', maxWidth: 1200, margin: '0 auto' }}>
          <Logo size="1.5rem" variant="white" />
          <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
            <button
              onClick={toggleTheme}
              style={{ background: 'rgba(255,255,255,0.15)', color: '#fff', border: '1px solid rgba(255,255,255,0.3)', padding: '0.5rem 0.6rem', borderRadius: 8, cursor: 'pointer', fontSize: '1.1rem', lineHeight: 1 }}
              title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
            >
              {theme === 'dark' ? '\u2600' : '\uD83C\uDF19'}
            </button>
            <button
              onClick={onLogin}
              style={{ background: 'rgba(255,255,255,0.15)', color: '#fff', border: '1px solid rgba(255,255,255,0.3)', padding: '0.6rem 1.5rem', borderRadius: 8, cursor: 'pointer', fontSize: '0.95rem', fontWeight: 500, fontFamily }}
            >
              Log In
            </button>
            <button
              onClick={BETA_MODE ? scrollToBetaForm : onSignup}
              style={{ background: '#fff', color: '#667eea', border: 'none', padding: '0.6rem 1.5rem', borderRadius: 8, cursor: 'pointer', fontSize: '0.95rem', fontWeight: 600, fontFamily }}
            >
              {BETA_MODE ? 'Request Beta Access' : 'Get Started'}
            </button>
          </div>
        </nav>

        <div style={{ maxWidth: 800, margin: '3rem auto 0', textAlign: 'center' }}>
          <h1 style={{ fontSize: '2.75rem', fontWeight: 800, letterSpacing: '-0.02em', lineHeight: 1.15, margin: '0 0 1.25rem' }}>
            Every Team Gets Fair Game Times. Guaranteed.
          </h1>
          <p style={{ fontSize: '1.15rem', opacity: 0.92, lineHeight: 1.6, maxWidth: 620, margin: '0 auto 2.5rem' }}>
            Fair scheduling, captain portals, email notifications, calendar sync, playoffs, and more — everything you need to run your rec league in one place.
          </p>
          <button
            onClick={BETA_MODE ? scrollToBetaForm : onSignup}
            style={{ background: '#fff', color: '#667eea', border: 'none', padding: '0.85rem 2rem', fontSize: '1rem', fontWeight: 700, borderRadius: 8, cursor: 'pointer', fontFamily }}
          >
            {BETA_MODE ? 'Request Beta Access' : 'Get Started Free'}
          </button>
        </div>
      </div>

      {/* ── Features ── */}
      <section style={{ padding: '5rem 2rem', background: colors.card }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <h2 style={{ textAlign: 'center', fontSize: '2rem', fontWeight: 700, color: colors.text, letterSpacing: '-0.02em', margin: '0 0 0.5rem' }}>
            Everything You Need to Run Your League
          </h2>
          <p style={{ textAlign: 'center', color: colors.textSecondary, fontSize: '1.1rem', margin: '0 0 3rem' }}>
            Built for rec leagues, not bloated enterprise software.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '2rem' }}>
            <FeatureCard
              icon={'\u2696'}
              title="Fair Scheduling Algorithm"
              desc="Our three-priority system guarantees every team gets exactly equal games at the earliest and latest time slots. No other scheduler does this."
              highlight
              colors={colors}
            />
            <FeatureCard
              icon={'\uD83D\uDC65'}
              title="Captain Portal"
              desc="Each captain gets a magic link to their own mobile-friendly page with their schedule, standings, and score submission."
              colors={colors}
            />
            <FeatureCard
              icon={'\u2709'}
              title="Email Notifications"
              desc="Automatically notify captains when the schedule is published, send game reminders, and score update emails."
              colors={colors}
            />
            <FeatureCard
              icon={'\uD83D\uDCC5'}
              title="Calendar Sync"
              desc="Players subscribe to their team's schedule in Google Calendar, Apple Calendar, or Outlook — games show up automatically."
              colors={colors}
            />
            <FeatureCard
              icon={'\uD83C\uDFDF'}
              title="Venue & Referee Management"
              desc="Assign games to fields with conflict detection, manage volunteer referees with their own portal and schedule view."
              colors={colors}
            />
            <FeatureCard
              icon={'\uD83C\uDFC6'}
              title="Playoff Brackets"
              desc="Single-elimination playoffs with automatic seeding from regular season standings. Score games and advance winners."
              colors={colors}
            />
            <FeatureCard
              icon={'\uD83D\uDCDA'}
              title="Season History"
              desc="Archive completed seasons and start fresh — all historical standings, schedules, and playoff results stay accessible."
              colors={colors}
            />
            <FeatureCard
              icon={'\uD83D\uDCC4'}
              title="PDF Export"
              desc="Beautiful branded PDF schedules with per-team pages, venue info, and a fairness report — ready for printing or sharing."
              colors={colors}
            />
          </div>
        </div>
      </section>

      {/* ── How It Works ── */}
      <section style={{ padding: '5rem 2rem', background: colors.sectionBg }}>
        <div style={{ maxWidth: 1000, margin: '0 auto' }}>
          <h2 style={{ textAlign: 'center', fontSize: '2rem', fontWeight: 700, color: colors.text, letterSpacing: '-0.02em', margin: '0 0 0.5rem' }}>
            How It Works
          </h2>
          <p style={{ textAlign: 'center', color: colors.textSecondary, fontSize: '1.1rem', margin: '0 0 3rem' }}>
            Four steps to a perfectly balanced schedule.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '2.5rem' }}>
            <Step number={1} title="Create Your League" desc="Name your league, add teams with captain emails, and set up venues." colors={colors} />
            <Step number={2} title="Set Dates & Time Slots" desc="Pick your game dates and available time slots for each week." colors={colors} />
            <Step number={3} title="Generate Your Schedule" desc="One click generates a perfectly balanced schedule with fair time distribution." colors={colors} />
            <Step number={4} title="Share & Manage" desc="Publish the schedule, notify captains, track scores, run playoffs, and archive seasons." colors={colors} />
          </div>
        </div>
      </section>

      {/* ── Pricing Preview ── */}
      <section style={{ padding: '5rem 2rem', background: colors.card, textAlign: 'center' }}>
        <div style={{ maxWidth: 900, margin: '0 auto' }}>
          <h2 style={{ fontSize: '2rem', fontWeight: 700, color: colors.text, letterSpacing: '-0.02em', margin: '0 0 0.5rem' }}>
            Simple Pricing
          </h2>
          <p style={{ color: colors.textSecondary, fontSize: '1.1rem', margin: '0 0 2rem' }}>
            Plans for every league size — no credit card required.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1.5rem', maxWidth: 780, margin: '0 auto' }}>
            <PricingMini name="Pickup" price="$0" period="/mo" desc="1 league, up to 8 teams, PDF with draft watermark" colors={colors} />
            <PricingMini name="League" price="$15" period="/mo" desc="1 league, unlimited teams, email notifications, venues" featured colors={colors} />
            <PricingMini name="Commissioner" price="$25" period="/mo" desc="Unlimited leagues, scores, playoffs, referees, season history" colors={colors} />
          </div>
          <p style={{ marginTop: '1.75rem', marginBottom: '0.75rem', fontSize: '0.95rem', color: colors.brand, fontWeight: 600 }}>
            Currently in beta — all features free during the beta period!
          </p>
          <Link to="/pricing" style={{ display: 'inline-block', color: colors.brand, fontSize: '1rem', fontWeight: 600, textDecoration: 'none' }}>
            View All Plans &rarr;
          </Link>
        </div>
      </section>

      {/* ── CTA Banner ── */}
      <section style={{ padding: '4rem 2rem', background: colors.sectionBg, textAlign: 'center' }}>
        <h2 style={{ fontSize: '2rem', fontWeight: 700, color: colors.text, margin: '0 0 1rem', letterSpacing: '-0.02em' }}>
          Ready to schedule your league?
        </h2>
        <p style={{ color: colors.textSecondary, fontSize: '1.1rem', margin: '0 0 2rem' }}>
          Join league organizers who trust LeagueMaker for fair, balanced schedules.
        </p>
        <button
          onClick={BETA_MODE ? scrollToBetaForm : onSignup}
          style={{ background: gradient, color: '#fff', border: 'none', padding: '0.85rem 2rem', fontSize: '1rem', fontWeight: 700, borderRadius: 8, cursor: 'pointer', fontFamily }}
        >
          {BETA_MODE ? 'Request Beta Access' : 'Create Your First League'}
        </button>
      </section>

      {/* ── Contact / Beta Request ── */}
      <section ref={betaFormRef} style={{ padding: '5rem 2rem', background: colors.card }}>
        <div style={{ maxWidth: 650, margin: '0 auto' }}>
          <h2 style={{ textAlign: 'center', fontSize: '2rem', fontWeight: 700, color: colors.text, letterSpacing: '-0.02em', margin: '0 0 0.5rem' }}>
            {BETA_MODE ? 'Request Beta Access' : 'Have Questions?'}
          </h2>
          <p style={{ textAlign: 'center', color: colors.textSecondary, fontSize: '1.1rem', margin: '0 0 2rem' }}>
            {BETA_MODE
              ? "We're onboarding league organizers for our free beta. Tell us about your league and we'll get you set up."
              : "We'd love to hear from you. Send us a message and we'll get back to you."}
          </p>
          {contactSent ? (
            <div style={{ textAlign: 'center', padding: '2rem 0' }}>
              <div style={{ fontSize: '3rem', color: colors.success, marginBottom: '1rem' }}>{'\u2713'}</div>
              <h3 style={{ color: colors.text, margin: '0 0 0.5rem' }}>
                {BETA_MODE ? 'Request Received!' : 'Message Sent!'}
              </h3>
              <p style={{ color: colors.textSecondary, margin: '0 0 2rem' }}>
                {BETA_MODE
                  ? "Thanks! We'll review your request and get back to you within 48 hours."
                  : 'Thanks for reaching out. We\'ll get back to you soon.'}
              </p>
              <button
                onClick={() => setContactSent(false)}
                style={{ background: gradient, color: '#fff', border: 'none', padding: '0.85rem 2rem', fontSize: '1rem', fontWeight: 600, borderRadius: 8, cursor: 'pointer', fontFamily }}
              >
                {BETA_MODE ? 'Submit Another Request' : 'Send Another Message'}
              </button>
            </div>
          ) : (
            <form onSubmit={handleContactSubmit} style={{ marginTop: '2rem' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.25rem' }}>
                <ContactField label="Name" type="text" value={contactName} onChange={setContactName} placeholder="Your name" required={BETA_MODE} colors={colors} />
                <ContactField label="Email" type="email" value={contactEmail} onChange={setContactEmail} placeholder="you@example.com" required colors={colors} />
              </div>
              {BETA_MODE ? (
                <>
                  <ContactField label="Organization / League Name" type="text" value={betaOrg} onChange={setBetaOrg} placeholder="Springfield Rec Softball League" required colors={colors} />
                  <div style={{ marginBottom: '1.25rem' }}>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500, color: colors.text, fontSize: '0.95rem' }}>Tell us about your league</label>
                    <textarea
                      value={betaDescription}
                      onChange={(e) => setBetaDescription(e.target.value)}
                      placeholder="Sport, number of teams, how you currently manage scheduling..."
                      rows={4}
                      required
                      style={{ width: '100%', padding: 12, border: `1px solid ${colors.inputBorder}`, borderRadius: 6, fontSize: '1rem', fontFamily: 'inherit', boxSizing: 'border-box', resize: 'vertical', minHeight: 100, background: colors.inputBg, color: colors.text }}
                    />
                  </div>
                </>
              ) : (
                <div style={{ marginBottom: '1.25rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500, color: colors.text, fontSize: '0.95rem' }}>Message</label>
                  <textarea
                    value={contactMessage}
                    onChange={(e) => setContactMessage(e.target.value)}
                    placeholder="What would you like to know?"
                    rows={5}
                    required
                    style={{ width: '100%', padding: 12, border: `1px solid ${colors.inputBorder}`, borderRadius: 6, fontSize: '1rem', fontFamily: 'inherit', boxSizing: 'border-box', resize: 'vertical', minHeight: 120, background: colors.inputBg, color: colors.text }}
                  />
                </div>
              )}
              {contactError && <div style={{ color: colors.danger, marginBottom: '1rem', fontSize: '0.9rem' }}>{contactError}</div>}
              <button
                type="submit"
                disabled={contactSending || !contactEmail.trim() || (BETA_MODE ? (!contactName.trim() || !betaOrg.trim() || !betaDescription.trim()) : !contactMessage.trim())}
                style={{
                  background: gradient, color: '#fff', border: 'none', padding: '0.85rem 2rem', fontSize: '1rem', fontWeight: 600, borderRadius: 8, cursor: 'pointer', width: '100%', fontFamily,
                  opacity: (contactSending || !contactEmail.trim()) ? 0.6 : 1,
                }}
              >
                {contactSending ? 'Sending...' : BETA_MODE ? 'Request Beta Access' : 'Send Message'}
              </button>
            </form>
          )}
        </div>
      </section>

      {/* ── Footer ── */}
      <footer style={{ padding: '2rem', textAlign: 'center', color: colors.textSecondary, fontSize: '0.9rem', background: colors.card, borderTop: `1px solid ${colors.border}` }}>
        <div style={{ marginBottom: '0.75rem' }}>
          <Link to="/help" style={{ color: colors.brand, textDecoration: 'none', fontWeight: 500 }}>Help &amp; How-To</Link>
          <span style={{ margin: '0 0.75rem', color: colors.border }}>|</span>
          <Link to="/faq" style={{ color: colors.brand, textDecoration: 'none', fontWeight: 500 }}>FAQ</Link>
          <span style={{ margin: '0 0.75rem', color: colors.border }}>|</span>
          <Link to="/pricing" style={{ color: colors.brand, textDecoration: 'none', fontWeight: 500 }}>Pricing</Link>
          <span style={{ margin: '0 0.75rem', color: colors.border }}>|</span>
          <Link to="/terms" style={{ color: colors.brand, textDecoration: 'none', fontWeight: 500 }}>Terms</Link>
          <span style={{ margin: '0 0.75rem', color: colors.border }}>|</span>
          <Link to="/privacy" style={{ color: colors.brand, textDecoration: 'none', fontWeight: 500 }}>Privacy</Link>
        </div>
        <p style={{ margin: 0 }}>&copy; {new Date().getFullYear()} LeagueMaker &middot; <a href="mailto:support@leaguemaker.app" style={{ color: colors.textSecondary, textDecoration: 'none' }}>support@leaguemaker.app</a></p>
      </footer>
    </div>
  );
}

/* ── Sub-components ── */

function FeatureCard({ icon, title, desc, highlight, colors }) {
  return (
    <div style={{
      padding: '2rem',
      borderRadius: 12,
      background: highlight ? 'linear-gradient(135deg, rgba(102,126,234,0.06) 0%, rgba(118,75,162,0.06) 100%)' : colors.sectionBg,
      border: highlight ? '2px solid rgba(102,126,234,0.25)' : `1px solid ${colors.border}`,
    }}>
      <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>{icon}</div>
      <h3 style={{ fontSize: '1.15rem', fontWeight: 600, color: colors.text, margin: '0 0 0.5rem', letterSpacing: '-0.01em' }}>{title}</h3>
      <p style={{ color: colors.textSecondary, lineHeight: 1.6, fontSize: '0.95rem', margin: 0 }}>{desc}</p>
    </div>
  );
}

function Step({ number, title, desc, colors }) {
  return (
    <div style={{ textAlign: 'center' }}>
      <div style={{
        width: 48, height: 48, borderRadius: '50%', background: gradient, color: '#fff',
        fontSize: '1.2rem', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1rem',
      }}>
        {number}
      </div>
      <h3 style={{ fontSize: '1.1rem', fontWeight: 600, color: colors.text, margin: '0 0 0.5rem' }}>{title}</h3>
      <p style={{ color: colors.textSecondary, lineHeight: 1.6, fontSize: '0.95rem', margin: 0 }}>{desc}</p>
    </div>
  );
}

function PricingMini({ name, price, period, desc, featured, colors }) {
  return (
    <div style={{
      background: featured ? colors.card : colors.sectionBg,
      border: featured ? 'none' : `1px solid ${colors.border}`,
      borderTop: `3px solid ${featured ? '#764ba2' : '#667eea'}`,
      borderRadius: 12,
      padding: '2rem 1.5rem',
      boxShadow: featured ? '0 4px 16px rgba(118,75,162,0.12)' : 'none',
    }}>
      <h3 style={{ fontSize: '1.15rem', fontWeight: 700, color: colors.text, margin: '0 0 0.5rem' }}>{name}</h3>
      <div style={{ fontSize: '2.25rem', fontWeight: 800, color: colors.text, marginBottom: '0.75rem' }}>
        {price}<span style={{ fontSize: '1rem', fontWeight: 500, color: colors.textSecondary }}>{period}</span>
      </div>
      <p style={{ color: colors.textSecondary, fontSize: '0.95rem', lineHeight: 1.5, margin: 0 }}>{desc}</p>
    </div>
  );
}

function ContactField({ label, type, value, onChange, placeholder, required, colors }) {
  return (
    <div style={{ marginBottom: '1.25rem' }}>
      <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500, color: colors.text, fontSize: '0.95rem' }}>{label}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        required={required}
        style={{ width: '100%', padding: 12, border: `1px solid ${colors.inputBorder}`, borderRadius: 6, fontSize: '1rem', fontFamily: 'inherit', boxSizing: 'border-box', background: colors.inputBg, color: colors.text }}
      />
    </div>
  );
}

export default LandingPage;
